// i18n réelle (Phase 0 du plan de migration) — charge le VRAI dictionnaire de
// StoreFlowProV2 (public/locales/*.json, porté tel quel dans src/locales/, copié dans
// dist/locales/ par build.js) et fournit un t(key, vars?, fallback?) minimal.
//
// Chantier traduction des pages (2026-08-03) : l'infrastructure existait déjà (Phase 0)
// mais aucun module n'appelait réellement t() — toute l'UI restait en français en dur
// malgré un dictionnaire de 2935 clés déjà porté pour les 5 langues. Ce chantier
// remplace le texte en dur par des appels t(), module par module (voir MEMORY pour le
// module pilote et le statut par module), en commençant par Accueil.
import { useState, useEffect, useCallback } from "react";

// Certaines clés portent une variante par nombre ({n}), au format ICU simplifié
// .one/.other (suffisant pour fr/de/en/es/it — ces 5 langues n'ont que ces 2
// catégories CLDR, jamais "few"/"many"/"dual"). {n} et toute autre variable dans la
// chaîne trouvée sont ensuite interpolés littéralement (remplacement de {nom}).
function interpolate(raw, vars) {
  if (!vars) return raw;
  return raw.replace(/\{(\w+)\}/g, (full, name) => (vars[name] !== undefined ? String(vars[name]) : full));
}

const dictCache = new Map(); // lang -> dictionnaire déjà chargé (jamais refetché deux fois)

async function loadDictionary(lang) {
  if (dictCache.has(lang)) return dictCache.get(lang);
  try {
    // "force-cache" servait indéfiniment une réponse déjà en cache HTTP sans jamais
    // revalider, même après un rechargement complet de la page — combiné à l'absence
    // d'en-tête Cache-Control côté serveur pour locales/*.json (voir server/index.js),
    // un client ayant chargé une langue avant un ajout de clés (chantier traduction)
    // continuait de voir l'ancien dictionnaire pour toujours. "no-store" force une
    // vraie requête réseau à chaque session ; dictCache évite déjà les refetch inutiles
    // au sein d'une même session.
    const res = await fetch(`/locales/${lang}.json`, { cache: "no-store" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const dict = await res.json();
    dictCache.set(lang, dict);
    return dict;
  } catch {
    // Hors-ligne / fichier absent — mieux vaut retomber sur les fallback fournis par
    // chaque appel t() que de bloquer l'app ou afficher une clé brute à l'utilisateur
    // (piège déjà documenté côté vanilla — voir storeflow_i18n_missing_key_shows_raw).
    return {};
  }
}

export function useI18n(lang) {
  const [dict, setDict] = useState(() => dictCache.get(lang) || null);

  useEffect(() => {
    let cancelled = false;
    loadDictionary(lang).then((d) => { if (!cancelled) setDict(d); });
    return () => { cancelled = true; };
  }, [lang]);

  // Deux formes d'appel : t(key, fallback) — comme avant — et t(key, {n, ...vars}, fallback)
  // quand la clé a besoin d'un compte (sélectionne .one/.other) et/ou de variables à
  // interpoler. Distingue les deux au type du 2e argument (chaîne = fallback direct,
  // objet = vars).
  const t = useCallback((key, arg2, arg3) => {
    const vars = arg2 && typeof arg2 === "object" ? arg2 : undefined;
    const fallback = vars ? arg3 : arg2;
    // N'utilise la forme .one/.other que si le dictionnaire la possède vraiment — sinon
    // une clé jamais pluralisée (ex. "{n} article(s)") retomberait systématiquement sur le
    // fallback JS (toujours en français) dans toutes les AUTRES langues, un bug silencieux
    // trouvé en pleine chantier traduction (2026-08-03) après coup sur plusieurs appels déjà
    // écrits (lieferschein.itemCountTemplate, haccp.weekNumberLabel, etc.).
    const pluralKey = vars && typeof vars.n === "number" ? `${key}.${vars.n === 1 ? "one" : "other"}` : null;
    const resolvedKey = pluralKey && dict && dict[pluralKey] !== undefined ? pluralKey : key;
    const value = dict ? dict[resolvedKey] : undefined;
    const raw = typeof value === "string" && value ? value : (fallback !== undefined ? fallback : key);
    return interpolate(raw, vars);
  }, [dict]);

  return { t, ready: dict !== null };
}
